import { Component, Inject, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ITrip, ITripModalData } from '../../time-sheets/time-sheets.type';
import { TimeSheetsStoreService } from '../../time-sheets/time-sheets.store';
import { TripTypeEnum } from 'app/pilotpay/time-sheets/trip-type.enum';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as moment from 'moment';
import * as _ from 'lodash';
import { TimeSheetStatusEnum } from '../../time-sheets/time-sheet-status.enum';
import { SessionService } from '../../../core';

/**
 * dialog for pilotpay
 */
@Component({
	selector: 'app-pilotpay-tripdialog',
	styleUrls: ['./tripdialog.component.scss'],
	templateUrl: './tripdialog.component.html'
})
export class TripDialogComponent implements OnInit {
	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<TripDialogComponent>,
		private timeSheetsStoreService: TimeSheetsStoreService,
		@Inject(MAT_DIALOG_DATA)
		public modalData: ITripModalData,
		private sessionService: SessionService
	) { }

	public isAdmin: boolean = this.sessionService.getUser().isAdmin;
	public errorMessage: string;
	public form: FormGroup;
	public isSubmitted: boolean = false;
	public minDateValue: moment.Moment;
	public maxEndDateValue: moment.Moment;
	public maxStartDateValue: moment.Moment;
	public startTimeHoursError: any;
	public startTimeMinutesError: any;
	public endTimeHoursError: any;
	public endTimeMinutesError: any;

	get tripTypeEnum(): any {
		return TripTypeEnum;
	}

	get timeSheetStatusEnum(): any {
		return TimeSheetStatusEnum;
	}

	// In the case a trip's edits are removed, converting flight hours to minutes will cause a loss of precision
	private static generatePayload(
		modalTripData: ITrip,
		userTripData: Partial<ITrip> & { flightHours: number }
	): ITrip {
		const payload: ITrip & { flightHours: number, flightMinutes: number } = { ...modalTripData, ...userTripData };
		payload.flightMinutes = ((Number(payload.flightHours) * 60) + Number(payload.flightMinutes));
		delete payload.flightHours;

		return payload as ITrip;
	}

	public ngOnInit(): void {

		this.form = this.fb.group({
			tripStart: [this.modalData.trip ? moment.utc(this.modalData.trip.tripStart) : null, Validators.required],
			tripEnd: [
				this.modalData.trip && this.modalData.trip.tripEnd ? moment.utc(this.modalData.trip.tripEnd) : null
			],
			tripNumber: [this.modalData.trip ? this.modalData.trip.tripNumber : null],
			routeInformation: [this.modalData.trip ? this.modalData.trip.routeInformation : null, Validators.required],
			flightHours: [this.modalData.trip ? this.calculateFlightHours(this.modalData.trip.flightMinutes) : ''],
			flightMinutes: [this.modalData.trip ? this.calculateFlightMinutes(this.modalData.trip.flightMinutes) : ''],
			loadsUnloadsCount: [
				this.modalData.trip ? this.modalData.trip.loadsUnloadsCount : null,
				Validators.required
			],
			comments: [this.modalData.trip ? this.modalData.trip.comments : null, Validators.required],
			timeSheetId: [this.modalData.timeSheet ? this.modalData.timeSheet.timeSheetId : null],
			tripType: [this.modalData.trip ? this.getTripTypeValue(this.modalData.trip) : this.tripTypeEnum.Standard],
			brokenTripCount: [
				this.modalData.trip ? this.modalData.trip.brokenTripCount : 0,
				this.modalData.trip && this.modalData.trip.brokenTripCount
					? [Validators.required, Validators.min(2)]
					: null
			]
		});

		this.minDateValue = moment.utc(this.modalData.timeSheet.startDate);
		this.maxStartDateValue = moment.utc(this.modalData.timeSheet.endDate);
		this.maxEndDateValue = moment
			.utc(this.modalData.timeSheet.endDate)
			.add(1, 'month')
			.set('date', this.modalData.timeSheet.timeSheetLockOutDay - 1)
			.endOf('day');

		// Flight Hours is required and Displayed for non-load specialits only
		if (!this.modalData.currentEmployee.isLoadSpecialist) {
			this.form.get('flightHours').setValidators([Validators.required]);
			this.form.get('flightMinutes').setValidators([Validators.required]);
		}

		this.form.addControl(
			'startTime',
			new FormControl(this.form.value.tripStart ? this.getTimeValue(this.form.value.tripStart) : '00:00', [
				Validators.required
			])
		);

		this.form.addControl(
			'endTime',
			new FormControl(this.form.value.tripEnd ? this.getTimeValue(this.form.value.tripEnd) : '00:00', [
				Validators.required
			])
		);

		this.form
			.get('startTime')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.startTimeHoursError = res.length !== 5;
					this.startTimeMinutesError = res.length !== 5;
					if (res.length === 5) {
						this.setBlockTime();
					}
				}
			});

		this.form
			.get('endTime')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.endTimeHoursError = res.length !== 5;
					this.endTimeMinutesError = res.length !== 5;
					if (res.length === 5) {
						this.setBlockTime();
					}
				}
			});

		this.form
			.get('tripStart')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.setBlockTime();
				}
			});


		this.form
			.get('tripEnd')
			.valueChanges.pipe(
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(res => {
				if (res) {
					this.setBlockTime();
				}
			});


		if (
			this.modalData.trip &&
			this.modalData.trip.timeSheetTripId &&
			this.modalData.currentEmployee.isLoadSpecialist &&
			this.modalData.trip.cmsOriginalTripStart
		) {
			const previousFormValue: FormGroup = this.form.value;
			this.form.valueChanges.pipe(distinctUntilChanged()).subscribe(currentFormValue => {
				let diffObj: any = [];
				if (currentFormValue) {
					diffObj = _.reduce(
						previousFormValue,
						(result: any, value: any, key: any) => {
							return _.isEqual(value, currentFormValue[key]) ? result : result.concat(key);
						},
						[]
					);
					if (
						diffObj.length === 1 &&
						_.includes(diffObj, 'loadsUnloadsCount') &&
						this.form.value.loadsUnloadsCount < 3
					) {
						this.form.get('comments').clearValidators();
					} else {
						this.form.get('comments').setValidators([Validators.required]);
					}

					this.form.get('comments').updateValueAndValidity({ onlySelf: true });
				}
			});
		}

		if (
			(this.modalData.timeSheetStatusError && !this.modalData.timeSheetLockOutError) ||
			this.modalData.timeSheetLockOutError || (this.modalData?.trip?.timeSheetTripId && !this.sessionService.getUser().isAdmin)
		) {
			this.form.disable();
		}
	}

	public getTimeValue(dateTime: moment.Moment): string {
		return dateTime ? dateTime.format('HH:mm') : null;
	}

	public addTrip(): void {
		this.errorMessage = null;

		if (
			this.form.invalid ||
			this.startTimeHoursError ||
			this.startTimeMinutesError ||
			this.endTimeHoursError ||
			this.endTimeMinutesError ||
			!this.form.value.startTime ||
			!this.form.value.endTime
		) {
			return;
		}

		const startTime: moment.Moment = this.getDate('tripStart', 'startTime');
		const endTime: moment.Moment = this.getDate('tripEnd', 'endTime');

		this.errorMessage = this.form.value.tripStart.isAfter(this.form.value.tripEnd)
			? 'Trip start date can not be after trip end date'
			: null;

		if (!this.form.invalid && !this.errorMessage) {
			this.isSubmitted = true;
			const formValue: any = this.form.value;
			formValue.tripStart = startTime.format('YYYY-MM-DDTHH:mm:ss');
			formValue.tripEnd = endTime.format('YYYY-MM-DDTHH:mm:ss');

			formValue.isBypassed = formValue.tripType === TripTypeEnum.Bypassed;
			formValue.isCanceled = formValue.tripType === TripTypeEnum.Canceled;

			delete formValue.tripType;

			const payload: ITrip = TripDialogComponent.generatePayload(this.modalData.trip, formValue);

			this.timeSheetsStoreService.addTrip(payload as ITrip).subscribe(
				(res: ITrip) => this.close(res),
				(error: HttpErrorResponse) => {
					this.errorMessage = error.error;
					this.isSubmitted = false;
				}
			);
		}
	}

	public calculateFlightHours(flightMinutes: number): string {
		return Math.floor(flightMinutes / 60).toString();
	}

	public calculateFlightMinutes(flightMinutes: number): string {
		return (flightMinutes % 60).toString();
	}

	public close(res?: ITrip): void {
		this.dialogRef.close(res);
	}

	public deleteTrip(): void {
		this.timeSheetsStoreService
			.deleteTrip(this.modalData.trip.timeSheetTripId)
			.subscribe(() => this.close(), (error: HttpErrorResponse) => (this.errorMessage = error.error));
	}

	public editTrip(): void {
		this.errorMessage = null;

		if (
			this.form.invalid ||
			this.startTimeHoursError ||
			this.startTimeMinutesError ||
			this.endTimeHoursError ||
			this.endTimeMinutesError ||
			!this.form.value.startTime ||
			!this.form.value.endTime
		) {
			return;
		}

		this.errorMessage = this.form.value.tripStart.isAfter(this.form.value.tripEnd)
			? 'Trip start date can not be after trip end date'
			: null;

		const isAdmin: boolean = this.sessionService.getUser().isAdmin;
		if (isAdmin && this.modalData.timeSheet.status === TimeSheetStatusEnum.ApprovedByAdmin) {
			this.errorMessage = 'Cannot edit an approved time sheet, please unapprove then try again';
			return;
		}

		const startTime: moment.Moment = this.getDate('tripStart', 'startTime');
		const endTime: moment.Moment = this.getDate('tripEnd', 'endTime');

		if (!this.form.invalid && !this.errorMessage) {
			this.isSubmitted = true;
			const formValue: any = this.form.value;
			formValue.tripStart = startTime.format('YYYY-MM-DDTHH:mm:ss');
			formValue.tripEnd = endTime.format('YYYY-MM-DDTHH:mm:ss');

			formValue.isBypassed = formValue.tripType === TripTypeEnum.Bypassed;
			formValue.isCanceled = formValue.tripType === TripTypeEnum.Canceled;

			delete formValue.tripType;

			const payload: ITrip = TripDialogComponent.generatePayload(
				this.modalData.trip,
				formValue
			);

			this.timeSheetsStoreService.updateTrip(this.modalData.trip.timeSheetTripId, payload).subscribe(
				(res: ITrip) => this.close(res),
				(error: HttpErrorResponse) => {
					this.errorMessage = error.error;
					this.isSubmitted = false;
				}
			);
		}
	}

	public hasEdits(): boolean {
		// Non-CMS trips are considered to not have any edits
		if (!this.modalData.trip.cmsOriginalTripStart) {
			return false;
		}

		const {
			cmsOriginalLoadsUnloadsCount,
			cmsOriginalRouteInformation,
			cmsOriginalTripNumber
		} = this.modalData.trip;
		const cmsOriginalFlightHours: string = this.calculateFlightHours(this.modalData.trip.cmsOriginalFlightMinutes);
		const cmsOriginalTripEnd: moment.Moment = moment.utc(this.modalData.trip.cmsOriginalTripEnd);
		const cmsOriginalTripStart: moment.Moment = moment.utc(this.modalData.trip.cmsOriginalTripStart);

		const { flightHours, loadsUnloadsCount, routeInformation, tripEnd, tripNumber, tripStart } = this.form.value;

		return (
			cmsOriginalFlightHours !== flightHours ||
			cmsOriginalLoadsUnloadsCount !== loadsUnloadsCount ||
			cmsOriginalRouteInformation !== routeInformation ||
			!cmsOriginalTripEnd.isSame(tripEnd) ||
			cmsOriginalTripNumber !== tripNumber ||
			!cmsOriginalTripStart.isSame(tripStart)
		);
	}

	// brokenTripCount control is conditionally required based on what type of trip this is
	public onTripTypeChange(tripType: string): void {
		if (tripType === this.tripTypeEnum.Broken) {
			this.form.patchValue({ brokenTripCount: 2 });
			this.form.get('brokenTripCount').setValidators([Validators.required, Validators.min(2)]);
		} else {
			this.form.patchValue({ brokenTripCount: 0 });
			this.form.get('brokenTripCount').clearValidators();
		}

		this.form.get('brokenTripCount').updateValueAndValidity();
	}

	public removeEdits(): void {
		// Non-CMS trips are considered to not have any edits
		if (!this.modalData.trip.cmsOriginalTripStart) {
			return;
		}

		const isAdmin: boolean = this.sessionService.getUser().isAdmin;

		if (isAdmin && this.modalData.timeSheet.status === TimeSheetStatusEnum.ApprovedByAdmin) {
			this.errorMessage = 'Cannot remove edits of approved time sheet, please unapprove then try again';
			return;
		}

		if (!isAdmin && this.modalData.trip.varianceIsApproved) {
			this.errorMessage = 'Cannot remove edits of approved change';
			return;
		}

		this.form.patchValue({
			tripStart: moment.utc(this.modalData.trip.cmsOriginalTripStart),
			tripEnd: this.modalData.trip.cmsOriginalTripEnd ? moment.utc(this.modalData.trip.cmsOriginalTripEnd) : null,
			tripNumber: this.modalData.trip.cmsOriginalTripNumber,
			routeInformation: this.modalData.trip.cmsOriginalRouteInformation,
			flightHours: this.calculateFlightHours(this.modalData.trip.cmsOriginalFlightMinutes),
			flightMinutes: this.calculateFlightHours(this.modalData.trip.cmsOriginalFlightMinutes),
			loadsUnloadsCount: this.modalData.trip.cmsOriginalLoadsUnloadsCount
		});

		const payload: ITrip = TripDialogComponent.generatePayload(this.modalData.trip, this.form.value);
		this.timeSheetsStoreService
			.updateTrip(this.modalData.trip.timeSheetTripId, payload)
			.subscribe(
				(res: ITrip) => this.close(res),
				(error: HttpErrorResponse) => (this.errorMessage = error.error)
			);
	}

	public onTimeChange($event: any, controlName: string): void {
		this.form.get(controlName).markAsDirty();
		if ($event.target.placeholder === 'HH') {
			if (!$event.target.value) {
				this.form.get(controlName).patchValue(null);
			}
			this[`${controlName}HoursError`] = !$event.target.value;
		}
		if ($event.target.placeholder === 'MM') {
			if (!$event.target.value) {
				this.form.get(controlName).patchValue(null);
			}
			this[`${controlName}MinutesError`] = !$event.target.value;
		}
	}

	public onCalendarSelect(element: any): void {
		element.inputfieldViewChild.nativeElement.focus();
	}

	private combineDateTimeValues(dateControl: string, timeControl: string): void {
		if (!this.form.value[dateControl] || !this.form.value[timeControl]) {
			return;
		}

		const tempDate: moment.Moment = moment.utc(this.form.value[dateControl]);
		tempDate.set('hours', this.form.value[timeControl].split(':')[0]);
		tempDate.set('minutes', this.form.value[timeControl].split(':')[1]);
		this.form.get(dateControl).patchValue(tempDate);
	}

	private getTripTypeValue(trip: ITrip): string {
		if (trip.brokenTripCount) {
			return this.tripTypeEnum.Broken;
		}

		if (trip.isCanceled) {
			return this.tripTypeEnum.Canceled;
		}

		if (trip.isBypassed) {
			return this.tripTypeEnum.Bypassed;
		}

		return this.tripTypeEnum.Standard;
	}

	private getDate(dateControl: string, timeControl: string): moment.Moment {
		const tempDate: moment.Moment = moment.utc(this.form.value[dateControl]);
		tempDate.set('hours', this.form.value[timeControl].split(':')[0]);
		tempDate.set('minutes', this.form.value[timeControl].split(':')[1]);
		return tempDate;
	}

	private setBlockTime(): void {
		const startDateTime: moment.Moment = this.getDate('tripStart', 'startTime');
		const endDateTime: moment.Moment = this.getDate('tripEnd', 'endTime');
		if (startDateTime.isValid() && endDateTime.isValid()) {
			const duration: moment.Duration = moment.duration(endDateTime.diff(startDateTime));
			const totalHours: number = Math.floor(duration.asHours());
			const remainingMinutes: number = duration.minutes();
			this.form.get("flightHours").patchValue(totalHours);
			this.form.get("flightHours").markAsDirty();
			this.form.get("flightMinutes").patchValue(remainingMinutes);
			this.form.get("flightMinutes").markAsDirty();
		}
	}
}
